import { forwardRef, useEffect, useMemo, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import { FormControl, styled, TablePagination } from "@mui/material";
import { FormPhoneMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_phone_masked_input";
import { FormRangedTimeMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_ranged_time_masked_input";
import { FormCPFMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_cpf_masked_input";
import { FormExperienciaInput } from "./form_experiencia_input";
import { FormProrrogarEmInput } from "./form_prorrogar_em_input";
import { FormMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_masked_input";
import { Cep } from "../../../../domain/models/cep";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { StyledDataGrid } from "../../../../../../common/components/styled_data_grid";
import { GenerateAfastamentoColumnsDefinition, GenerateCentroCustoSecundarioColumnsDefinition, GenerateDependenteColumnsDefinition, GenerateEventoColumnsDefinition, GenerateFeriasColumnsDefinition } from "../columns";
import { Dependente } from "../../../../domain/models/dependente";
import { Evento } from "../../../../domain/models/evento";
import { Ferias } from "../../../../domain/models/ferias";
import { Afastamento } from "../../../../domain/models/afastamento";
import { FuncionarioForm } from "../../../schemas/Funcionarios_cadastro/funcionario_schema";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { MotivoDemissaoDropDown } from "../../../../domain/models/motivo_demissao_dropdown";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import { FormSelectInput } from "../../../../../../common/components/masked_inputs/react_forms/form_select_input";
import { Funcionario } from "../../../../domain/models/funcionario";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../../../common/hooks/use_debounced_callback";
import { CentroCustoResume } from "../../../../../../types/comercial/centroCusto";
import { CentroCustoSecundario } from "../../../../domain/models/centro_custo_secundario";
import { DropDownCargo } from "../../../../../../common/components/dropdown_cargo";
import { DefaultFormMoneyInput } from "../../../../../../common/components/default_form_money_input";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import FormContainer from "../../../../../../common/components/form_container";
import PrimaryButton from "../../../../../../common/components/primary_button";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import CommonConstants from "../../../../../../common/constants";

/**
 * VisuallyHiddenInput
 */

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

/**
 * TabInformacoesGeraisContent
 */

type Props = {
  loading: boolean,
  isCreated: boolean,
  funcionario: Funcionario | null,
  fileUpload: string | null,
  centroCustoLista: CentroCustoDropDown[],
  motivosDemissaoLista: MotivoDemissaoDropDown[],
  hookForm: UseFormReturn<FuncionarioForm>,

  centroCustoSecundarioPage: number,
  centroCustoSecundarioRowsPerPage: number,
  centroCustoSecundarioTotalItems: number,
  centroCustoSecundarioLista: CentroCustoResume[],

  dependentesPage: number,
  dependentesRowsPerPage: number,
  dependentesTotalItems: number,
  dependentesLista: Dependente[],

  eventosPage: number,
  eventosRowsPerPage: number,
  eventosTotalItems: number,
  eventosLista: Evento[],

  feriasPage: number,
  feriasRowsPerPage: number,
  feriasTotalItems: number,
  feriasLista: Ferias[],

  afastamentosPage: number,
  afastamentosRowsPerPage: number,
  afastamentosTotalItems: number,
  afastamentosLista: Afastamento[],

  onStartFileUpload: (file: File | null) => void,
  onDeleteFileUpload: () => void,
  onLoadCep: (cep: string) => Promise<Cep | undefined>,

  onCreateCentroCusto: () => void,
  onCreateMotivoDemissao: () => void,

  onSetCadastroCentroCustoSecundarioOpenState: (idDependente: number | null, openState: boolean) => void,
  onSetCadastroDependenteOpenState: (idDependente: number | null, openState: boolean) => void,
  onSetCadastroEventoOpenState: (idEvento: number | null, openState: boolean) => void,
  onSetCadastroFeriasOpenState: (idFerias: number | null, openState: boolean) => void,
  onSetCadastroAfastamentoOpenState: (idAfastamento: number | null, openState: boolean) => void,

  onStartDeleteCentroCustoSecundario: (idDependente: number, openState: boolean) => void,
  onStartDeleteDependente: (idDependente: number, openState: boolean) => void,
  onStartDeleteEvento: (idEvento: number, openState: boolean) => void,
  onStartDeleteFerias: (idFerias: number, openState: boolean) => void,
  onStartDeleteAfastamento: (idAfastamento: number, openState: boolean) => void,

  onCentroCustoSecundarioPageChange: (page: number) => void,
  onCentroCustoSecundarioRowsPerPageChange: (rowsPerPage: number) => void,

  onDependentesPageChange: (page: number) => void,
  onDependentesRowsPerPageChange: (rowsPerPage: number) => void,

  onEventosPageChange: (page: number) => void,
  onEventosRowsPerPageChange: (rowsPerPage: number) => void,

  onFeriasPageChange: (page: number) => void,
  onFeriasRowsPerPageChange: (rowsPerPage: number) => void,

  onAfastamentoPageChange: (page: number) => void,
  onAfastamentoRowsPerPageChange: (rowsPerPage: number) => void,
};

export const TabInformacoesGeraisContent = forwardRef<HTMLButtonElement, Props>(({
  loading,
  isCreated,
  funcionario,
  fileUpload,
  centroCustoLista,
  motivosDemissaoLista,
  hookForm,

  centroCustoSecundarioPage,
  centroCustoSecundarioRowsPerPage,
  centroCustoSecundarioTotalItems,
  centroCustoSecundarioLista,

  dependentesPage,
  dependentesRowsPerPage,
  dependentesTotalItems,
  dependentesLista,

  eventosPage,
  eventosRowsPerPage,
  eventosTotalItems,
  eventosLista,

  feriasPage,
  feriasRowsPerPage,
  feriasTotalItems,
  feriasLista,

  afastamentosPage,
  afastamentosRowsPerPage,
  afastamentosTotalItems,
  afastamentosLista,

  onStartFileUpload,
  onDeleteFileUpload,
  onLoadCep,

  onCreateCentroCusto,
  onCreateMotivoDemissao,

  onSetCadastroCentroCustoSecundarioOpenState,
  onSetCadastroDependenteOpenState,
  onSetCadastroEventoOpenState,
  onSetCadastroFeriasOpenState,
  onSetCadastroAfastamentoOpenState,

  onStartDeleteCentroCustoSecundario,
  onStartDeleteDependente,
  onStartDeleteEvento,
  onStartDeleteFerias,
  onStartDeleteAfastamento,

  onCentroCustoSecundarioPageChange,
  onCentroCustoSecundarioRowsPerPageChange,

  onDependentesPageChange,
  onDependentesRowsPerPageChange,

  onEventosPageChange,
  onEventosRowsPerPageChange,

  onFeriasPageChange,
  onFeriasRowsPerPageChange,

  onAfastamentoPageChange,
  onAfastamentoRowsPerPageChange,
}, ref) => {
  const onLoadCepSkiper = useRef(false);
  const debounceHandler = useCreateDebounceHandler(750);

  const [valeTransporte] = hookForm.watch(['valeTransporte']);
  const hasValeTransporte = ((typeof valeTransporte === "string" && valeTransporte === "1") ||
    (typeof valeTransporte === "number" && valeTransporte === 1));

  const centroCustoSecundarioColumnsDefinition = useMemo(() => {
    return GenerateCentroCustoSecundarioColumnsDefinition({
      handleActionEdit: (idCentroCustoSecundario) => onSetCadastroCentroCustoSecundarioOpenState(idCentroCustoSecundario, true),
      handleActionDelete: (idCentroCustoSecundario) => onStartDeleteCentroCustoSecundario(idCentroCustoSecundario, true),
    });
  }, [onSetCadastroCentroCustoSecundarioOpenState, onStartDeleteCentroCustoSecundario]);

  const dependenteColumnsDefinition = useMemo(() => {
    return GenerateDependenteColumnsDefinition({
      handleActionEdit: (idDependente) => onSetCadastroDependenteOpenState(idDependente, true),
      handleActionDelete: (idDependente) => onStartDeleteDependente(idDependente, true),
    });
  }, [onSetCadastroDependenteOpenState, onStartDeleteDependente]);

  const eventosColumnsDefinition = useMemo(() => {
    return GenerateEventoColumnsDefinition({
      handleActionEdit: (idEvento) => onSetCadastroEventoOpenState(idEvento, true),
      handleActionDelete: (idEvento) => onStartDeleteEvento(idEvento, true),
    });
  }, [onSetCadastroEventoOpenState, onStartDeleteEvento]);

  const feriasColumnsDefinition = useMemo(() => {
    return GenerateFeriasColumnsDefinition({
      handleActionEdit: (idFerias) => onSetCadastroFeriasOpenState(idFerias, true),
      handleActionDelete: (idFerias) => onStartDeleteFerias(idFerias, true),
    });
  }, [onSetCadastroFeriasOpenState, onStartDeleteFerias]);

  const afastamentoColumnsDefinition = useMemo(() => {
    return GenerateAfastamentoColumnsDefinition({
      handleActionEdit: (idAfastamento) => onSetCadastroAfastamentoOpenState(idAfastamento, true),
      handleActionDelete: (idAfastamento) => onStartDeleteAfastamento(idAfastamento, true),
    });
  }, [onSetCadastroAfastamentoOpenState, onStartDeleteAfastamento]);

  const loadCep = useDebouncedCallback(debounceHandler, async (cep: string) => {
    return async () => {
      if (typeof cep === "string") {
        const normalizedCep = cep.trim();
        const requiredSize = 'xx.xxx-xxx';

        if (normalizedCep.length === requiredSize.length) {
          const cepInfo = await onLoadCep(cep);
          if (cepInfo) {
            hookForm.setValue('enderecoEndereco', cepInfo.logradouro.toUpperCase());
            hookForm.setValue('enderecoBairro', cepInfo.bairro.toUpperCase());
            hookForm.setValue('enderecoCidade', cepInfo.cidade.toUpperCase());
            hookForm.setValue('enderecoEstado', cepInfo.uf.toUpperCase());
            hookForm.setValue('enderecoPais', cepInfo.pais.toUpperCase());
          }
        }
        else if (normalizedCep.length === 0) {
          hookForm.setValue('enderecoEndereco', '');
          hookForm.setValue('enderecoBairro', '');
          hookForm.setValue('enderecoCidade', '');
          hookForm.setValue('enderecoEstado', '');
          hookForm.setValue('enderecoPais', '');
        }
      }
    };
  }, [hookForm, onLoadCep]);

  useEffect(() => {
    if (funcionario) {
      onLoadCepSkiper.current = true;
    }
  }, [funcionario]);

  const tabelaCentroCustoSecundario = useMemo(() => {
    return (
      <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
        <StyledDataGrid
          tableName="tab_informacoes_gerais_content_1"
          disableColumnMenu={false}
          getRowId={(row: CentroCustoSecundario) => row.idCentroCustoSecundario}
          columns={centroCustoSecundarioColumnsDefinition}
          loading={loading}
          height='35vh'
          rows={centroCustoSecundarioLista}
          rowCount={centroCustoSecundarioLista.length}
          pageSizeOptions={[5]}
          paginationMode="server"
          paginationModel={{
            page: centroCustoSecundarioPage,
            pageSize: centroCustoSecundarioRowsPerPage,
          }} />

        <TablePagination
          component="div"
          count={centroCustoSecundarioTotalItems}
          page={centroCustoSecundarioPage - 1}
          rowsPerPageOptions={[5]}
          onPageChange={(_, p) => onCentroCustoSecundarioPageChange(p)}
          rowsPerPage={centroCustoSecundarioRowsPerPage}
          onRowsPerPageChange={(e) => onCentroCustoSecundarioRowsPerPageChange(parseInt(e.target.value))} />

      </div>
    );
  }, [
    loading,
    centroCustoSecundarioLista,
    centroCustoSecundarioPage,
    centroCustoSecundarioRowsPerPage,
    centroCustoSecundarioTotalItems,
    centroCustoSecundarioColumnsDefinition,
    onCentroCustoSecundarioPageChange,
    onCentroCustoSecundarioRowsPerPageChange,
  ]);

  const tabelaDependentes = useMemo(() => {
    return (
      <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
        <StyledDataGrid
          tableName="tab_informacoes_gerais_content_2"
          disableColumnMenu={false}
          getRowId={(row: Dependente) => row.idDependente}
          columns={dependenteColumnsDefinition}
          loading={loading}
          height='35vh'
          rows={dependentesLista}
          rowCount={dependentesLista.length}
          pageSizeOptions={[5]}
          paginationMode="server"
          paginationModel={{
            page: dependentesPage,
            pageSize: dependentesRowsPerPage,
          }} />

        <TablePagination
          component="div"
          count={dependentesTotalItems}
          page={dependentesPage - 1}
          rowsPerPageOptions={[5]}
          onPageChange={(_, p) => onDependentesPageChange(p)}
          rowsPerPage={dependentesRowsPerPage}
          onRowsPerPageChange={(e) => onDependentesRowsPerPageChange(parseInt(e.target.value))} />

      </div>
    );
  }, [
    loading,
    dependentesLista,
    dependentesPage,
    dependentesRowsPerPage,
    dependentesTotalItems,
    dependenteColumnsDefinition,
    onDependentesPageChange,
    onDependentesRowsPerPageChange,
  ]);

  const tabelaEventos = useMemo(() => {
    return (
      <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
        <StyledDataGrid
          tableName="tab_informacoes_gerais_content_3"
          disableColumnMenu={false}
          getRowId={(row: Evento) => row.idItemVerbaFuncionario}
          columns={eventosColumnsDefinition}
          loading={loading}
          height='35vh'
          rows={eventosLista}
          rowCount={eventosLista.length}
          pageSizeOptions={[5]}
          paginationMode="server"
          paginationModel={{
            page: eventosPage,
            pageSize: eventosRowsPerPage,
          }} />

        <TablePagination
          component="div"
          count={eventosTotalItems}
          page={eventosPage - 1}
          rowsPerPageOptions={[5]}
          onPageChange={(_, p) => onEventosPageChange(p)}
          rowsPerPage={eventosRowsPerPage}
          onRowsPerPageChange={(e) => onEventosRowsPerPageChange(parseInt(e.target.value))} />

      </div>
    );
  }, [
    loading,
    eventosLista,
    eventosPage,
    eventosRowsPerPage,
    eventosTotalItems,
    eventosColumnsDefinition,
    onEventosPageChange,
    onEventosRowsPerPageChange,
  ]);

  const tabelaFerias = useMemo(() => {
    return (
      <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
        <StyledDataGrid
          tableName="tab_informacoes_gerais_content_4"
          disableColumnMenu={false}
          getRowId={(row: Ferias) => row.idFerias}
          columns={feriasColumnsDefinition}
          loading={loading}
          height='35vh'
          rows={feriasLista}
          rowCount={feriasLista.length}
          pageSizeOptions={[5]}
          paginationMode="server"
          paginationModel={{
            page: feriasPage,
            pageSize: feriasRowsPerPage,
          }} />

        <TablePagination
          component="div"
          count={feriasTotalItems}
          page={feriasPage - 1}
          rowsPerPageOptions={[5]}
          onPageChange={(_, p) => onFeriasPageChange(p)}
          rowsPerPage={feriasRowsPerPage}
          onRowsPerPageChange={(e) => onFeriasRowsPerPageChange(parseInt(e.target.value))} />
      </div>
    );
  }, [
    loading,
    feriasLista,
    feriasPage,
    feriasRowsPerPage,
    feriasTotalItems,
    feriasColumnsDefinition,
    onFeriasPageChange,
    onFeriasRowsPerPageChange,
  ]);

  const tabelaAfastamento = useMemo(() => {
    return (
      <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
        <StyledDataGrid
          tableName="tab_informacoes_gerais_content_"
          disableColumnMenu={false}
          getRowId={(row: Afastamento) => row.idAfastamento}
          columns={afastamentoColumnsDefinition}
          loading={loading}
          rows={afastamentosLista}
          rowCount={afastamentosLista.length}
          pageSizeOptions={[5]}
          height='35vh'
          autoPageSize={true}
          paginationMode="server"
          paginationModel={{
            page: afastamentosPage,
            pageSize: afastamentosRowsPerPage,
          }}
        />

        <TablePagination
          component="div"
          count={afastamentosTotalItems}
          page={afastamentosPage - 1}
          rowsPerPageOptions={[5]}
          onPageChange={(_, p) => onAfastamentoPageChange(p)}
          rowsPerPage={afastamentosRowsPerPage}
          onRowsPerPageChange={(e) => onAfastamentoRowsPerPageChange(parseInt(e.target.value))}
        />

      </div>
    );
  }, [
    loading,
    afastamentosLista,
    afastamentosPage,
    afastamentosRowsPerPage,
    afastamentosTotalItems,
    afastamentoColumnsDefinition,
    onAfastamentoPageChange,
    onAfastamentoRowsPerPageChange,
  ]);

  return (
    <>
      <>
        <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode">
          <FormContainer title={"Foto 3x4"} arrangement="fullRow">
            <>
              <div className="w-[16rem] h-[12rem] border border-gray-300 py-2 px-6">
                <div className="w-full h-full" onClick={() => document.getElementById('profileInput')?.click()}>
                  {
                    fileUpload !== null ? (
                      <img
                        id='funcionarioImagem'
                        src={fileUpload}
                        className="w-full h-full object-contain bg-center bg-no-repeat"
                        alt="User profile" />
                    ) : (
                      <img
                        id='funcionarioImagem'
                        src={CommonConstants.assets.noAvatar}
                        className="w-full h-full object-contain bg-center bg-no-repeat"
                        alt="User profile" />
                    )
                  }
                </div>
              </div>
            </>
          </FormContainer>
          <div className="w-full flex flex-row justify-between mt-2">
            <p className="flex-1 text-black dark:text-white">São permitidas apenas imagens com extensão .png, .jpeg, .jpg, .gif, .img!</p>
            <div className="flex flex-row gap-2">
              <PrimaryButton
                component="label"
                id='profileInput'
                sx={{ paddingLeft: 4, paddingRight: 4 }}>
                <VisuallyHiddenInput
                  type="file"
                  id='profileImageInput'
                  onChange={(e) => onStartFileUpload(e.target.files?.item(0) ?? null)}
                  accept=".png,.jpeg,.jpg,.gif,.img"
                  multiple={false}
                />
                Selecionar
              </PrimaryButton>
              <PrimaryButton
                onClick={() => onDeleteFileUpload()}
                buttonColor='red'>
                Excluir
              </PrimaryButton>
            </div>
          </div>
        </div>
      </>
      <>
        <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
          <FormContainer title={"Informações Pessoais"}>
            <>
              <DefaultFormInput
                name={'nome'}
                label={"Nome"}
                required={true}
                reactHookForm={hookForm}
              />

              <FormDatePickerInput
                name='dataAdmissao'
                label="Data da admissão"
                required={true}
                reactHookForm={hookForm}
              />

              <DefaultFormAutocomplete
                name='idCentroCusto'
                label='Centro de Custo'
                required={true}
                getOptionLabel={(x) => x.title}
                getOptionKey={(x) => x.id}
                options={centroCustoLista}
                reactHookForm={hookForm}
                onNew={() => onCreateCentroCusto()}
              />

              <FormSelectInput
                name='sexo'
                label='Sexo'
                required={true}
                options={[
                  { id: 'F', label: 'Feminino' },
                  { id: 'M', label: 'Masculino' },
                ]}
                reactHookForm={hookForm}
              />

              <DropDownCargo
                name='idCargo'
                required={true}
                reactHookForm={hookForm}
              />

              <FormDatePickerInput
                name='dataNascimento'
                label="Data de nascimento"
                reactHookForm={hookForm}
              />

              <FormSelectInput
                name='regime'
                label='Regime de Contratação'
                options={[
                  { id: "C", label: 'CLT' },
                  { id: "E", label: 'Estagiário' },
                  { id: "P", label: 'PJ' },
                  { id: "D", label: 'Diarista' },
                  { id: "S", label: 'Sócio' },
                  { id: "J", label: 'Jovem' },
                ]}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'setor'}
                label={"Setor"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'filial'}
                label={"Filial"}
                reactHookForm={hookForm}
              />

              <FormCPFMaskedInput
                name='cpf'
                label='CPF'
                reactHookForm={hookForm}
              />

              <div className="grid gap-6 col-span-full grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3">
                <FormControl className="col-span-1 2xl:col-span-2">
                  <FormMaskedInput
                    name={'avaliarHabilidadesEm'}
                    label={"Avaliar Habilidades em"}
                    mask='0000'
                    maskPlaceholder="____"
                    reactHookForm={hookForm}
                  />

                  <p className="text-xs text-black dark:text-white">
                    Informe o tempo em dias para a 1ª avaliação de habilidades ou 0 para funcionários que não serão avaliados
                  </p>
                </FormControl>

                <FormRadioGroupInput
                  name='descontoSindical'
                  label='Desconto Sindical'
                  options={[
                    { id: 1, label: 'Sim' },
                    { id: 0, label: 'Não' },
                    { id: 2, label: 'Não se aplica' },
                  ]}
                  reactHookForm={hookForm}
                />

              </div>


              <DefaultFormInput
                name={'email1'}
                label={"Email"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'email2'}
                label={"Email secundário"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'matricula'}
                label={"Matricula"}
                reactHookForm={hookForm}
              />

              <FormPhoneMaskedInput
                name='telefoneMovel'
                type='movel'
                label='Tel. Celular'
                reactHookForm={hookForm} />

              <FormPhoneMaskedInput
                name='telefoneFixo'
                type='fixo'
                label='Tel. Fixo'
                reactHookForm={hookForm} />

              <FormRangedTimeMaskedInput
                name='horarioTrabalho'
                label={"Horário de Trabalho"}
                reactHookForm={hookForm} />

              <FormRangedTimeMaskedInput
                name='horarioAlmoco'
                label={"Tempo de Almoço"}
                reactHookForm={hookForm} />

              <DefaultFormInput
                name={'nacionalidade'}
                label={"Nacionalidade"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'naturalidade'}
                label={"Naturalidade"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'estado'}
                label={"Estado"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'numeroPis'}
                label={"Nº PIS"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'estadoCivil'}
                label={"Estado Civil"}
                reactHookForm={hookForm}
              />

              <DefaultFormMoneyInput
                name='salario'
                label={"Salário"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'periodo'}
                label={"Período"}
                reactHookForm={hookForm}
              />

              <FormExperienciaInput
                name='experiencia'
                label={"Experiência"}
                reactHookForm={hookForm}
              />

              <FormProrrogarEmInput
                name='prorrogarEm'
                label={"Prorrogar Em"}
                reactHookForm={hookForm}
              />

              <FormRadioGroupInput
                name='aprovadoExperiencia'
                label='Aprovado no período de experiência?'
                showCleanButton={true}
                options={[
                  { id: 0, label: 'Não' },
                  { id: 1, label: 'Sim' },
                ]}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'escolaridade'}
                label={"Escolaridade"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'cargaHorarioMensal'}
                label={"Carga horária Mensal"}
                reactHookForm={hookForm}
              />

              <FormRadioGroupInput
                name='valeTransporte'
                label='Vale Transporte?'
                options={[
                  { id: 0, label: 'Não' },
                  { id: 1, label: 'Sim' },
                ]}
                reactHookForm={hookForm} />


              {hasValeTransporte &&
                <DefaultFormMoneyInput
                  name='valorValeTransporte'
                  label={"Valor do Vale Transporte"}
                  reactHookForm={hookForm} />
              }
            </>
          </FormContainer>

          <FormContainer title={"Horário de trabalho"}>
            <>
              <FormRangedTimeMaskedInput
                name='horarioTrabalhoSegQui'
                label="Segunda a Quinta"
                reactHookForm={hookForm}
              />

              <FormRangedTimeMaskedInput
                name='horarioTrabalhoSex'
                label="Sexta"
                reactHookForm={hookForm}
              />
            </>
          </FormContainer>

          <FormContainer title={"CTPS"}>
            <>
              <DefaultFormInput
                name={'ctps'}
                label={"Número"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'ctpsSerie'}
                label={"Série"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'ctpsEstado'}
                label={"UF"}
                reactHookForm={hookForm}
              />

              <FormDatePickerInput
                name='ctpsDataEmissao'
                label="Data Emissão"
                reactHookForm={hookForm} />

            </>
          </FormContainer>

          <FormContainer title={"RG"}>
            <>
              <DefaultFormInput
                name={'rg'}
                label={"Número"}
                reactHookForm={hookForm}
              />

              <FormDatePickerInput
                name='rgDataExped'
                label="Data de Expedição"
                reactHookForm={hookForm} />

              <DefaultFormInput
                name={'rgOrgaoExped'}
                label={"Órgão Exped."}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'rgEstado'}
                label={"UF"}
                reactHookForm={hookForm}
              />
            </>
          </FormContainer>

          <FormContainer title={"Titulo de Eleitor"}>
            <>
              <DefaultFormInput
                name={'tituloEleitor'}
                label={"Número"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'tituloZona'}
                label={"Zona"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'tituloSecao'}
                label={"Seção"}
                reactHookForm={hookForm}
              />
            </>
          </FormContainer>

          <FormContainer title={"Certificado militar"}>
            <>
              <DefaultFormInput
                name={'reservista'}
                label={"Reservista"}
                reactHookForm={hookForm}
              />
            </>
          </FormContainer>

          <FormContainer title={"Filiação"}>
            <>
              <DefaultFormInput
                name={'nomeMae'}
                label={"Nome da Mãe"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'nomePai'}
                label={"Nome do Pai"}
                reactHookForm={hookForm}
              />
            </>
          </FormContainer>

          <FormContainer title={"Informações Cônjuge"}>
            <>
              <DefaultFormInput
                name={'conjugeNome'}
                label={"Nome"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'conjugeRg'}
                label={"RG"}
                reactHookForm={hookForm}
              />

              <FormCPFMaskedInput
                name='conjugeCpf'
                label='CPF'
                reactHookForm={hookForm}
              />
            </>
          </FormContainer>

          <FormContainer title={"Informações Complementares"}>
            <>
              <DefaultFormInput
                name={'complementoCamisaInfo'}
                label={"Camisa"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'complementoCalcaInfo'}
                label={"Calça"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'complementoCalcadoInfo'}
                label={"Calçado"}
                reactHookForm={hookForm}
              />
            </>
          </FormContainer>

          <FormContainer title={"Endereço"}>
            <>
              <FormMaskedInput
                name='enderecoCep'
                label={"CEP"}
                unmask={false}
                mask="00.000-000"
                maskPlaceholder="__.___-___"
                reactHookForm={hookForm}
                onBlur={(cep) => {
                  const normalizedCep = cep.trim().replaceAll('_', '');
                  const requiredSize = 'xx.xxx-xxx';

                  if (!onLoadCepSkiper.current) {
                    if (normalizedCep.length === 0 || normalizedCep.length === requiredSize.length) {
                      loadCep(cep);
                    }
                  }
                  onLoadCepSkiper.current = false;
                }}

              />

              <DefaultFormInput
                name={"enderecoCidade"}
                label={"Cidade"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={"enderecoEndereco"}
                label={"Endereço"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={"enderecoNumero"}
                label={"Número"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={"enderecoComplemento"}
                label={"Complemento"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={"enderecoBairro"}
                label={"Bairro"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={"enderecoEstado"}
                label={"Estado"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={"enderecoPais"}
                label={"País"}
                reactHookForm={hookForm}
              />
            </>
          </FormContainer>
        </div>
      </>

      <>
        <h1 className="font-bold text-black dark:text-white text-3xl mt-8 mb-2">Informações Bancárias</h1>
        <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
          <FormContainer title={"Informações Bancárias"}>
            <>
              <FormRadioGroupInput
                name='bancTipoInfo'
                label='Tipo de Conta?'
                options={[
                  { id: 0, label: 'Corrente' },
                  { id: 1, label: 'Poupança' },
                ]}
                reactHookForm={hookForm} />

              <DefaultFormInput
                name='bancBancoInfo'
                label='Banco'
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name='bancAgenciaInfo'
                label='Agência'
                reactHookForm={hookForm}
              />

              <div className="flex flex-row gap-1 items-center">
                <DefaultFormInput
                  name='bancContaInfo'
                  label='Conta'
                  reactHookForm={hookForm}
                />

                <p className="text-black dark:text-white">-</p>

                <div className="w-[7rem]">
                  <DefaultFormInput
                    name='bancContaDigitoInfo'
                    label={""}
                    reactHookForm={hookForm}
                  />
                </div>
              </div>

              <DefaultFormInput
                name={'bancOperacaoInfo'}
                label={"Operação"}
                reactHookForm={hookForm}
              />

              <DefaultFormInput
                name={'bancPixInfo'}
                label={"Pix"}
                reactHookForm={hookForm}
              />
            </>
          </FormContainer>
        </div>
      </>

      {isCreated &&
        <>
          <div className="w-full flex flex-row justify-between items-center mt-8 mb-2">
            <h1 className="font-bold text-black dark:text-white text-3xl">Centro de Custo Secundários</h1>
            <div>
              <PrimaryButton
                startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                sx={{ px: 2 }}
                onClick={() => onSetCadastroCentroCustoSecundarioOpenState(null, true)}>
                Novo
              </PrimaryButton>
            </div>
          </div>
          {tabelaCentroCustoSecundario}
        </>
      }

      {isCreated &&
        <>
          <div className="w-full flex flex-row justify-between items-center mt-8 mb-2">
            <h1 className="font-bold text-black dark:text-white text-3xl">Cadastro de Dependentes</h1>
            <div>
              <PrimaryButton
                startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                sx={{ px: 2 }}
                onClick={() => onSetCadastroDependenteOpenState(null, true)}>
                Novo
              </PrimaryButton>
            </div>
          </div>
          {tabelaDependentes}
        </>
      }

      {isCreated &&
        <>
          <div className="w-full flex flex-row justify-between items-center mt-8 mb-2">
            <h1 className="font-bold text-black dark:text-white text-3xl">Eventos</h1>
            <div>
              <PrimaryButton
                startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                sx={{ px: 2 }}
                onClick={() => onSetCadastroEventoOpenState(null, true)}>
                Novo
              </PrimaryButton>
            </div>
          </div>
          {tabelaEventos}
        </>
      }

      {isCreated &&
        <>
          <div className="w-full flex flex-row justify-between items-center mt-8 mb-2">
            <h1 className="font-bold text-black dark:text-white text-3xl">Cadastro de Férias</h1>
            <div>
              <PrimaryButton
                startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                sx={{ px: 2 }}
                onClick={() => onSetCadastroFeriasOpenState(null, true)}>
                Novo
              </PrimaryButton>
            </div>
          </div>
          {tabelaFerias}
        </>
      }

      {isCreated &&
        <>
          <div className="w-full flex flex-row justify-between items-center mt-8 mb-2">
            <h1 className="font-bold text-black dark:text-white text-3xl">Afastamentos</h1>
            <div>
              <PrimaryButton
                startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                sx={{ px: 2 }}
                onClick={() => onSetCadastroAfastamentoOpenState(null, true)}>
                Novo
              </PrimaryButton>
            </div>
          </div>
          {tabelaAfastamento}
        </>
      }

      <>
        <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-4">
          <FormContainer title={"Observações"}>
            <>
              <div className="col-span-2">
                <DefaultFormInput
                  name={'observacaoGeral'}
                  label={"Observações"}
                  multiline={true}
                  rows={7}
                  reactHookForm={hookForm}
                />
              </div>
            </>
          </FormContainer>
        </div>
      </>

      <>
        <div className="grid grid-cols-3 gap-6 w-full bg-light-mode-2 dark:bg-dark-mode-2 p-4 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode mb-16">
          <FormDatePickerInput
            name='demissaoData'
            label="Data Demissão"
            reactHookForm={hookForm}
          />

          <DefaultFormAutocomplete
            name='demissaoIdDemissao'
            label='Motivo de Demissão'
            newButtonLabel='Novo motivo de demissão'
            getOptionLabel={(x) => x.descricao ?? ''}
            getOptionKey={(x) => x.idMotivoDemissao}
            options={motivosDemissaoLista}
            reactHookForm={hookForm}
            onNew={() => onCreateMotivoDemissao()}
          />

          <div className="flex flex-row gap-2 items-center">
            <FormDatePickerInput
              name='inicioAvisoPrevio'
              label="Início Aviso Prévio"
              reactHookForm={hookForm}
            />

            <FormDatePickerInput
              name='fimAvisoPrevio'
              label="Término Aviso Prévio"
              reactHookForm={hookForm}
            />

          </div>


        </div>
      </>
    </>
  );
});